import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { formatearMonto } from '@/core/utils/formatters';
const WIDTH = 560;
const HEIGHT = 180;
const PAD_X = 12;
const PAD_TOP = 16;
const PAD_BOTTOM = 32;
/** Convierte "2026-05" en "May 26" para etiqueta corta. */
function etiquetaMes(mes) {
    const [yyyy, mm] = mes.split('-');
    if (!yyyy || !mm)
        return mes;
    const fecha = new Date(Number(yyyy), Number(mm) - 1, 1);
    if (Number.isNaN(fecha.getTime()))
        return mes;
    const corto = fecha.toLocaleDateString('es-AR', { month: 'short' });
    return `${corto.charAt(0).toUpperCase() + corto.slice(1).replace('.', '')} ${yyyy.slice(2)}`;
}
export function ComisionesPorMesChart({ datos }) {
    const [hoverIdx, setHoverIdx] = useState(null);
    const { barras, max, total } = useMemo(() => {
        if (datos.length === 0)
            return { barras: [], max: 0, total: 0 };
        const max = Math.max(1, ...datos.map((d) => d.total));
        const innerH = HEIGHT - PAD_TOP - PAD_BOTTOM;
        const groupWidth = (WIDTH - PAD_X * 2) / datos.length;
        const barWidth = Math.min(groupWidth * 0.6, 56);
        const barras = datos.map((d, i) => {
            const cx = PAD_X + i * groupWidth + groupWidth / 2;
            const h = (d.total / max) * innerH;
            const baseline = PAD_TOP + innerH;
            return {
                mes: d.mes,
                etiqueta: etiquetaMes(d.mes),
                total: d.total,
                x: cx - barWidth / 2,
                y: baseline - h,
                width: barWidth,
                height: h,
                groupX: PAD_X + i * groupWidth,
                groupWidth,
                baseline,
            };
        });
        const total = datos.reduce((s, d) => s + d.total, 0);
        return { barras, max, total };
    }, [datos]);
    if (datos.length === 0 || max === 0) {
        return (_jsx("div", { className: "flex h-[180px] items-center justify-center text-sm text-text-tertiary", children: "Sin comisiones registradas en los \u00FAltimos meses." }));
    }
    const activa = hoverIdx !== null ? barras[hoverIdx] : null;
    const TT_W = 140;
    const TT_H = 44;
    let ttX = activa ? activa.groupX + activa.groupWidth / 2 - TT_W / 2 : 0;
    if (activa) {
        if (ttX < PAD_X)
            ttX = PAD_X;
        if (ttX + TT_W > WIDTH - PAD_X)
            ttX = WIDTH - PAD_X - TT_W;
    }
    const ttY = activa ? Math.max(2, activa.y - TT_H - 6) : 0;
    return (_jsxs("div", { className: "w-full", children: [_jsx("div", { className: "mb-3 flex flex-wrap items-baseline gap-x-5 gap-y-1", children: _jsxs("div", { className: "flex flex-col", children: [_jsxs("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: ["Total devengado \u00B7 \u00FAltimos ", datos.length, " meses"] }), _jsx("span", { className: "num text-xl font-bold text-success", children: formatearMonto(total) })] }) }), _jsxs("svg", { viewBox: `0 0 ${WIDTH} ${HEIGHT}`, role: "img", "aria-label": "Ingresos generados por mes", className: "h-[180px] w-full", children: [_jsx("line", { x1: PAD_X, y1: HEIGHT - PAD_BOTTOM, x2: WIDTH - PAD_X, y2: HEIGHT - PAD_BOTTOM, stroke: "rgb(var(--color-border-subtle))", strokeWidth: "1" }), barras.map((b, i) => {
                        const activo = hoverIdx === i;
                        return (_jsxs("g", { onMouseEnter: () => setHoverIdx(i), onMouseLeave: () => setHoverIdx((v) => (v === i ? null : v)), children: [_jsx("rect", { x: b.groupX, y: PAD_TOP, width: b.groupWidth, height: HEIGHT - PAD_TOP - PAD_BOTTOM, className: activo ? 'fill-text-primary/5 cursor-pointer' : 'fill-transparent cursor-pointer' }), _jsx("rect", { x: b.x, y: b.y, width: b.width, height: b.height, rx: "3", className: "fill-success", opacity: hoverIdx !== null && !activo ? 0.55 : 1 }), _jsx("text", { x: b.groupX + b.groupWidth / 2, y: HEIGHT - PAD_BOTTOM + 14, textAnchor: "middle", className: "fill-current text-[11px] font-medium text-text-secondary", children: b.etiqueta })] }, b.mes));
                    }), activa ? (_jsxs("g", { pointerEvents: "none", children: [_jsx("rect", { x: ttX, y: ttY, width: TT_W, height: TT_H, rx: "6", fill: "rgb(var(--color-surface-3))", stroke: "rgb(var(--color-border-subtle))", strokeWidth: "1" }), _jsx("text", { x: ttX + 8, y: ttY + 14, className: "fill-current text-[10px] font-medium text-text-tertiary", children: activa.etiqueta }), _jsx("text", { x: ttX + 8, y: ttY + 32, className: "fill-current text-[12px] font-semibold text-text-primary", children: formatearMonto(activa.total) })] })) : null] })] }));
}
